import React, { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

export function AdvAccountPage() {
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ["adv-portal", "account"],
    queryFn: async () => {
      const res = await api.get("/v1/portal/advertiser/account");
      if (!res.ok) throw new Error("Failed to load account");
      return (await res.json()).data;
    },
  });

  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");

  useEffect(() => {
    if (data) {
      setFirstName(data.user?.firstName || "");
      setLastName(data.user?.lastName || "");
    }
  }, [data]);

  const saveMut = useMutation({
    mutationFn: async () => {
      const res = await api.patch("/v1/portal/advertiser/account", { firstName, lastName });
      if (!res.ok) throw new Error("Failed to update");
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["adv-portal", "account"] }),
  });

  if (isLoading) {
    return <div className="animate-pulse space-y-4"><div className="h-8 bg-gray-200 rounded w-1/4" /><div className="h-64 bg-gray-200 rounded" /></div>;
  }

  return (
    <div className="space-y-6 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Account Settings</h1>
        <p className="text-sm text-gray-500 mt-1">Manage your profile</p>
      </div>

      <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-5">
        <h3 className="text-sm font-semibold text-gray-900">Profile</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">First Name</label>
            <input value={firstName} onChange={(e) => setFirstName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Last Name</label>
            <input value={lastName} onChange={(e) => setLastName(e.target.value)}
              className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent" />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
          <input readOnly value={data?.user?.email || ""} className="w-full rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" />
        </div>
      </div>

      {data?.advertiser && (
        <div className="bg-white rounded-xl border border-gray-200 p-6 space-y-3">
          <h3 className="text-sm font-semibold text-gray-900">Company Info</h3>
          <dl className="text-sm space-y-2">
            <div className="flex justify-between">
              <dt className="text-gray-500">Company Name</dt>
              <dd className="text-gray-700">{data.advertiser.name}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Platform</dt>
              <dd className="text-gray-700">{data.advertiser.platformName || "-"}</dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-gray-500">Status</dt>
              <dd className="capitalize text-gray-700">{data.advertiser.status}</dd>
            </div>
          </dl>
        </div>
      )}

      <div className="flex justify-end">
        <button
          onClick={() => saveMut.mutate()}
          disabled={saveMut.isPending}
          className="px-6 py-2.5 rounded-lg bg-blue-600 text-white text-sm font-medium hover:bg-blue-700 disabled:opacity-50 transition-colors"
        >
          {saveMut.isPending ? "Saving..." : "Save Changes"}
        </button>
      </div>

      {saveMut.isSuccess && (
        <p className="text-sm text-blue-600">Changes saved successfully.</p>
      )}
    </div>
  );
}
